import { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface DayStripProps {
  selectedDate: Date
  onDateChange: (date: Date) => void
  /** Optional slot rendered on the left side (e.g. Masters / Today shortcuts) */
  leftSlot?: React.ReactNode
  /** Optional slot rendered on the right side (e.g. date picker + view mode toggle) */
  children?: React.ReactNode
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

interface DayPill {
  key: string
  date: Date
  isToday: boolean
  label: string
}

// Window is 8 days wide; the selected day sits at this index so it reads centered.
const WINDOW_SIZE = 8
const SELECTED_OFFSET = 3

export function DayStrip({ selectedDate, onDateChange, leftSlot, children }: DayStripProps) {
  const today = useMemo(() => startOfDay(new Date()), [])

  // Build an 8-day window centered on the selected day so it sits in the middle
  // of the strip (selected day at index SELECTED_OFFSET).
  const days = useMemo(() => {
    const start = addDays(startOfDay(selectedDate), -SELECTED_OFFSET)

    const pills: DayPill[] = []
    for (let i = 0; i < WINDOW_SIZE; i++) {
      const d = addDays(start, i)
      const isT = isSameDay(d, today)
      pills.push({
        key: d.toISOString().slice(0, 10),
        date: d,
        isToday: isT,
        label: isT
          ? 'Today'
          : d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
      })
    }
    return pills
  }, [selectedDate, today])

  // Chevrons step the selected day by one (the window re-centers on it).
  const stepDay = (direction: number) => {
    onDateChange(addDays(startOfDay(selectedDate), direction))
  }

  return (
    <div className="flex items-center gap-2 px-4 py-1">
      {/* Left slot (Masters / Today shortcuts) */}
      {leftSlot && <div className="shrink-0">{leftSlot}</div>}

      {/* Day navigation — centered: left chevron + pills + right chevron */}
      <div className="flex flex-1 items-center justify-center gap-1 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => stepDay(-1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>

        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {days.map((day) => {
            const isSelected = isSameDay(day.date, startOfDay(selectedDate))
            return (
              <Button
                key={day.key}
                variant={isSelected ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 px-2.5 text-xs shrink-0 ${
                  day.isToday && !isSelected
                    ? 'font-bold text-primary'
                    : ''
                }`}
                onClick={() => onDateChange(day.date)}
              >
                {day.label}
              </Button>
            )
          })}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => stepDay(1)}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Right-side slot (date picker + view mode toggle) */}
      {children && <div className="shrink-0">{children}</div>}
    </div>
  )
}
